import { rmb, CONTRACT_VALUE_CEILING_NOTE, CONTRACT_VALUE_LEGACY_CAVEAT, TAX_PCT_MISSING_HINT, type ContractValueView } from '../lib/api';

/**
 * §1.11 当前核定合同价 — the two numbers, and the sentence that keeps them apart.
 *
 * Eric 9/18 (工程指令): extracted out of InstructionsPanel in PR10b so the SAME block, with the SAME
 * caveat wording, appears everywhere a contract's worth is read: the 工程指令 ledger, the 执行与结算 tab
 * header and beside the certified amount on a payment cert. Three hand-written variants of 「付款上限仍按
 * 基准合同价计算」 is exactly how one of them ends up quietly saying something else, so the copy lives in
 * `CONTRACT_VALUE_CEILING_NOTE` / `CONTRACT_VALUE_LEGACY_CAVEAT` and is never retyped.
 *
 * 改改 9/20 (budget): the headline figure is 含税. Every number a budget or 商务 person quotes, reconciles
 * or pays against is tax-inclusive — the ex-tax ledger figure and the 税率 stay underneath, small, so the
 * two are readable together and never confusable. When the lock carries no 税率 the server sends every
 * `*_incl_tax_rmb` as null; the headline then falls back to the ex-tax figure and SAYS SO in warn colour,
 * because a 9% gap shown as if it were the contract price is the whole failure this block exists to stop.
 *
 * Read-only and advisory by construction — it renders numbers and never offers an action. The payment
 * ceilings (合同 4.2.3/4.2.4) are computed server-side off `baseline_value_rmb` and nothing here moves them.
 */
export default function ContractValueBlock({ cv, title, compact }: {
  cv: ContractValueView;
  /** Optional heading — defaults to the 含税 wording for the full / compact shapes. */
  title?: string;
  /** Beside a certified amount there is no room for six cells — show the three that matter. */
  compact?: boolean;
}) {
  const hasTax = cv.tax_pct != null;
  const heading = title ?? (compact
    ? '计量参考（含税）/ Reference for this certificate (tax-inclusive)'
    : '当前核定合同价（含税）/ Current approved contract value (tax-inclusive)');

  /** One figure, read 含税-first: the big number is tax-inclusive, the ex-tax ledger figure sits under it. */
  const cell = (label: string, ex: number | null, incl: number | null, strong?: boolean) => (
    <div style={{ minWidth: compact ? 110 : 130, wordBreak: 'break-word' }}>
      <div className="small muted">{label}</div>
      <div style={{ fontWeight: strong ? 700 : 500, fontSize: strong ? (compact ? 16 : 18) : 15 }}>
        {rmb(hasTax ? incl : ex)}
      </div>
      {hasTax
        ? <div className="small muted">不含税 {rmb(ex)} · 税率 {cv.tax_pct}%</div>
        : <div className="small" style={{ color: 'var(--warn)' }}>不含税 / pre-tax</div>}
    </div>
  );

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>{heading}</div>
      <div className="row-wrap" style={{ gap: compact ? 14 : 18 }}>
        {cell('基准合同价 / Baseline', cv.baseline_rmb, cv.baseline_incl_tax_rmb)}
        {!compact && cell('已认价增项 / Approved additions', cv.additions_rmb, cv.additions_incl_tax_rmb)}
        {!compact && cell('减项 / Omissions', cv.omissions_rmb, cv.omissions_incl_tax_rmb)}
        {!compact && cell('计日工 / Daywork', cv.daywork_rmb, cv.daywork_incl_tax_rmb)}
        {cell('当前核定合同价 / Current approved', cv.current_approved_rmb, cv.current_approved_incl_tax_rmb, true)}
        {cell('待认价（参考）/ Pending (advisory)', cv.pending_rmb, cv.pending_incl_tax_rmb)}
      </div>
      {!hasTax && (
        <div className="small" style={{ marginTop: 8, color: 'var(--warn)' }}>⚠ {TAX_PCT_MISSING_HINT}</div>
      )}
      <div className="small" style={{ marginTop: 8, color: 'var(--accent)' }}>{CONTRACT_VALUE_CEILING_NOTE}</div>
      {cv.baseline_includes_legacy && (
        <div className="small muted" style={{ marginTop: 4 }}>
          {CONTRACT_VALUE_LEGACY_CAVEAT}（旧版变更已并入 {rmb(hasTax ? cv.legacy_incorporated_incl_tax_rmb : cv.legacy_incorporated_rmb)}{hasTax ? '，含税' : '，不含税'}）
        </div>
      )}
    </div>
  );
}
